import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Gift, Loader2, CheckCircle2, Mail, CreditCard, Landmark, FileDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Reveal, ChristmasLights } from "@/components/effects/Effects";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { type: "e", buyer_name: "", buyer_email: "", recipient_name: "", message: "", payment_method: "card", shipping_address: "", num_players: 2, gdpr_consent: false };

export default function Vouchers() {
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { transfer, code } | { paid, code }
  const [checking, setChecking] = useState(false);
  const [zoom, setZoom] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get("/vouchers/public-info").then(({ data }) => {
      setInfo(data);
      setForm((p) => ({ ...p, payment_method: data.online_enabled ? "card" : "transfer" }));
    }).catch(() => {});
  }, []);

  const pollVoucher = useCallback(async (sid, attempts = 0) => {
    if (attempts >= 10) { setChecking(false); toast.error("Platba se nepotvrdila včas. Zkontrolujte e-mail."); return; }
    try {
      const { data } = await api.get(`/payments/status/${sid}`);
      if (data.payment_status === "paid") {
        setChecking(false);
        setResult({ paid: true, code: data.voucher_code });
        setOpen(true);
        window.history.replaceState({}, "", "/");
        return;
      }
      setTimeout(() => pollVoucher(sid, attempts + 1), 2000);
    } catch (e) { setChecking(false); }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("voucher_session_id");
    const cancelled = params.get("voucher_cancelled");
    if (sid) { setChecking(true); pollVoucher(sid); }
    else if (cancelled) { toast.info("Nákup poukazu byl zrušen."); window.history.replaceState({}, "", "/"); }
  }, [pollVoucher]);

  const submit = async () => {
    if (!form.buyer_name || !form.buyer_email) { toast.error("Vyplňte jméno a e-mail."); return; }
    if (form.type === "physical" && !form.shipping_address) { toast.error("Zadejte doručovací adresu."); return; }
    if (!form.gdpr_consent) { toast.error("Musíte souhlasit se zpracováním osobních údajů."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/vouchers/purchase", { ...form, origin_url: window.location.origin });
      if (data.requires_payment) { window.location.href = data.checkout_url; return; }
      setResult({ transfer: true, code: data.code });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nákup se nepodařilo dokončit.");
    } finally { setSubmitting(false); }
  };

  const perPerson = info?.pricing_mode === "per_person";
  const pppV = info?.price_per_person || {};
  const price = perPerson ? Number(pppV[String(form.num_players)] ?? info?.price ?? 2500) : (info?.price ?? 2500);
  const online = info?.online_enabled ?? true;
  const physicalOn = info?.physical_enabled !== false;
  const previewImg = info?.preview_image || "";
  const previewW = info?.preview_width || 320;

  const openDialog = () => { setForm({ ...EMPTY, payment_method: online ? "card" : "transfer" }); setResult(null); setOpen(true); };

  return (
    <section id="poukazy" className="relative bg-[#0b0706] border-b border-white/10 overflow-hidden" data-testid="vouchers-section">
      <ChristmasLights count={10} />
      <div className="max-w-4xl mx-auto px-5 py-20 lg:py-24 text-center relative z-10">
        <Reveal>
          <Gift className="w-10 h-10 text-nv mx-auto mb-5 glow-nv" />
          <p className="text-xs tracking-[0.4em] text-nv uppercase mb-3">DÁRKOVÝ POUKAZ</p>
          <h2 className="font-head text-3xl sm:text-4xl text-neutral-100 mb-4 vhs-glitch" data-testid="voucher-section-title">{info?.section_title || "Darujte strach"}</h2>
          {info?.section_intro ? (
            <p className="text-base text-neutral-400 max-w-xl mx-auto mb-2 leading-relaxed whitespace-pre-line" data-testid="voucher-section-intro">{info.section_intro}</p>
          ) : (
            <p className="text-base text-neutral-400 max-w-xl mx-auto mb-2 leading-relaxed">
              Věnujte svým blízkým zážitek, na který nikdy nezapomenou. Dárkový poukaz na hru HUBERT v hodnotě
              <span className="text-neutral-100 font-bold"> {price} Kč</span>{physicalOn ? " — elektronicky i fyzicky." : " — elektronicky."}
            </p>
          )}
          {previewImg && (
            <div className="my-6 flex justify-center">
              <img src={previewImg} alt="Náhled dárkového poukazu" onClick={() => setZoom(true)}
                style={{ maxWidth: `${previewW}px` }}
                className="w-full h-auto cursor-zoom-in border border-white/10 hover:border-nv transition-colors"
                data-testid="voucher-preview-image" />
            </div>
          )}
          {(info?.image_e || info?.image_physical) && (
            <div className="flex justify-center gap-4 my-6 flex-wrap">
              {info.image_e && <img src={info.image_e} alt="E-poukaz" className="h-32 sm:h-40 object-contain border border-white/10" data-testid="voucher-preview-e" />}
              {physicalOn && info.image_physical && <img src={info.image_physical} alt="Fyzický poukaz" className="h-32 sm:h-40 object-contain border border-white/10" data-testid="voucher-preview-physical" />}
            </div>
          )}
          <button onClick={openDialog} data-testid="voucher-buy-btn"
            className="hud-btn vhs-btn inline-block font-head tracking-[0.2em] px-8 py-4 text-sm mt-4">
            [ KOUPIT POUKAZ ]
          </button>
        </Reveal>
      </div>

      {/* PREVIEW ZOOM LIGHTBOX */}
      <Dialog open={zoom && !!previewImg} onOpenChange={setZoom}>
        <DialogContent className="bg-black/95 border-white/10 rounded-none max-w-4xl p-2" data-testid="voucher-preview-lightbox">
          <DialogTitle className="sr-only">Náhled dárkového poukazu</DialogTitle>
          {previewImg && <img src={previewImg} alt="Náhled dárkového poukazu" className="w-full h-auto max-h-[85vh] object-contain" data-testid="voucher-preview-zoomed" />}
        </DialogContent>
      </Dialog>

      {/* CHECKING OVERLAY */}
      <Dialog open={checking} onOpenChange={() => {}}>
        <DialogContent className="bg-[#0a0a0a] border-t-2 border-t-nv border-white/10 rounded-none max-w-md text-center text-neutral-200">
          <DialogTitle className="sr-only">Ověřuji platbu</DialogTitle>
          <div className="py-8"><Loader2 className="w-12 h-12 text-nv animate-spin mx-auto mb-4" /><p className="font-head uppercase tracking-widest">Ověřuji platbu…</p></div>
        </DialogContent>
      </Dialog>

      {/* PURCHASE / RESULT DIALOG */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0a0a0a] border-t-2 border-t-nv border-white/10 rounded-none max-w-lg text-neutral-200 max-h-[90vh] overflow-y-auto" data-testid="voucher-dialog">
          {result ? (
            <div className="py-4 text-center" data-testid="voucher-result">
              <CheckCircle2 className="w-14 h-14 text-nv mx-auto mb-4 glow-nv" />
              {result.paid ? (
                <>
                  <h3 className="font-head text-2xl uppercase text-neutral-100 mb-2">Poukaz zakoupen!</h3>
                  <p className="text-sm text-neutral-400 mb-4">Poslali jsme ho na e-mail. Kód poukazu:</p>
                  <p className="font-mono text-2xl text-nv border border-nv/40 py-3 mb-4 tracking-widest">{result.code}</p>
                  {result.code && <a href={`${BACKEND}/api/vouchers/pdf/${result.code}`} target="_blank" rel="noreferrer" className="hud-btn vhs-btn inline-flex items-center gap-2 px-5 py-2 text-sm font-head"><FileDown className="w-4 h-4" /> Stáhnout PDF</a>}
                </>
              ) : (
                <>
                  <h3 className="font-head text-2xl uppercase text-neutral-100 mb-2">Objednávka přijata</h3>
                  <p className="text-sm text-neutral-400 mb-2">Platební údaje jsme poslali na váš e-mail. Poukaz aktivujeme po přijetí platby.</p>
                  <p className="text-xs text-neutral-500 mb-3">Kód poukazu:</p>
                  <p className="font-mono text-xl text-nv border border-nv/40 py-3 tracking-widest">{result.code}</p>
                  {info?.bank_info && <pre className="text-xs text-neutral-400 whitespace-pre-wrap text-left border border-white/10 p-3 mt-4 bg-black">{info.bank_info}</pre>}
                </>
              )}
              <button onClick={() => setOpen(false)} className="hud-btn vhs-btn mt-6 px-6 py-2 font-head text-sm">ZAVŘÍT</button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-head uppercase text-neutral-100 tracking-wide">Koupit dárkový poukaz</DialogTitle>
                <DialogDescription className="text-neutral-500">Hodnota poukazu: <span className="text-nv font-bold">{price} Kč</span> · platnost {info?.validity_months ?? 12} měsíců</DialogDescription>
              </DialogHeader>

              <div>
                <Label className="text-xs text-neutral-400 mb-2 block">Typ poukazu</Label>
                <RadioGroup value={form.type} onValueChange={(v) => set("type", v)} className={`grid ${physicalOn ? "grid-cols-2" : "grid-cols-1"} gap-2`}>
                  <label className={`flex items-center gap-2 cursor-pointer text-sm border p-3 ${form.type === "e" ? "border-nv bg-nv/10 text-neutral-100" : "border-white/10 text-neutral-400"}`}>
                    <RadioGroupItem value="e" data-testid="voucher-type-e" className="border-nv text-nv" /> <Mail className="w-4 h-4" /> E-poukaz (PDF)
                  </label>
                  {physicalOn && (
                    <label className={`flex items-center gap-2 cursor-pointer text-sm border p-3 ${form.type === "physical" ? "border-nv bg-nv/10 text-neutral-100" : "border-white/10 text-neutral-400"}`}>
                      <RadioGroupItem value="physical" data-testid="voucher-type-physical" className="border-nv text-nv" /> <Gift className="w-4 h-4" /> Fyzický
                    </label>
                  )}
                </RadioGroup>
              </div>

              {perPerson && (
                <div data-testid="voucher-players">
                  <Label className="text-xs text-neutral-400 mb-2 block">Počet hráčů (určuje cenu)</Label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4].map((n) => (
                      <button key={n} type="button" onClick={() => set("num_players", n)} data-testid={`voucher-players-${n}`}
                        className={`flex-1 py-2 border text-sm font-head ${form.num_players === n ? "border-nv bg-nv/10 text-nv" : "border-white/10 text-neutral-400"}`}>{n}</button>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-neutral-400">Vaše jméno *</Label>
                  <Input data-testid="voucher-buyer-name" value={form.buyer_name} onChange={(e) => set("buyer_name", e.target.value)} className="rounded-none bg-[#111] border-white/10 focus-visible:ring-nv mt-1" /></div>
                <div><Label className="text-xs text-neutral-400">Váš e-mail *</Label>
                  <Input data-testid="voucher-buyer-email" type="email" value={form.buyer_email} onChange={(e) => set("buyer_email", e.target.value)} className="rounded-none bg-[#111] border-white/10 focus-visible:ring-nv mt-1" /></div>
              </div>
              <div><Label className="text-xs text-neutral-400">Jméno obdarovaného (nepovinné)</Label>
                <Input data-testid="voucher-recipient" value={form.recipient_name} onChange={(e) => set("recipient_name", e.target.value)} className="rounded-none bg-[#111] border-white/10 focus-visible:ring-nv mt-1" /></div>
              <div><Label className="text-xs text-neutral-400">Vzkaz (nepovinné)</Label>
                <Textarea data-testid="voucher-message" value={form.message} onChange={(e) => set("message", e.target.value)} rows={2} className="rounded-none bg-[#111] border-white/10 focus-visible:ring-nv mt-1" /></div>
              {form.type === "physical" && (
                <div><Label className="text-xs text-neutral-400">Doručovací adresa *</Label>
                  <Textarea data-testid="voucher-shipping" value={form.shipping_address} onChange={(e) => set("shipping_address", e.target.value)} rows={2} className="rounded-none bg-[#111] border-white/10 focus-visible:ring-nv mt-1" placeholder="Jméno, ulice, město, PSČ" /></div>
              )}

              <div>
                <Label className="text-xs text-neutral-400 mb-2 block">Způsob platby</Label>
                <RadioGroup value={form.payment_method} onValueChange={(v) => set("payment_method", v)} className="gap-2">
                  {online && (
                    <label className="flex items-center gap-3 cursor-pointer text-sm text-neutral-300 border border-white/10 p-3">
                      <RadioGroupItem value="card" data-testid="voucher-pay-card" className="border-nv text-nv" /> <CreditCard className="w-4 h-4" /> Kartou online (okamžitě)
                    </label>
                  )}
                  <label className="flex items-center gap-3 cursor-pointer text-sm text-neutral-300 border border-white/10 p-3">
                    <RadioGroupItem value="transfer" data-testid="voucher-pay-transfer" className="border-nv text-nv" /> <Landmark className="w-4 h-4" /> Bankovní převod
                  </label>
                </RadioGroup>
              </div>

              <div className="flex items-start gap-3 border border-white/10 p-3 bg-[#111]">
                <Checkbox data-testid="voucher-gdpr" id="vgdpr" checked={form.gdpr_consent}
                  onCheckedChange={(v) => set("gdpr_consent", !!v)}
                  className="rounded-none border-nv data-[state=checked]:bg-nv data-[state=checked]:text-black mt-0.5" />
                <Label htmlFor="vgdpr" className="text-xs text-neutral-400 cursor-pointer leading-relaxed">Souhlasím se zpracováním osobních údajů (GDPR). *</Label>
              </div>

              <button onClick={submit} disabled={submitting} data-testid="voucher-submit-btn"
                className="hud-btn vhs-btn font-head tracking-widest py-4 w-full flex items-center justify-center">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> :
                  form.payment_method === "card" ? `ZAPLATIT ${price} KČ` : `OBJEDNAT (${price} KČ PŘEVODEM)`}
              </button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
